/**
 * The assistant's live call in the browser: the socket, the microphone, the
 * speaker, the words as they arrive, and the two levels.
 *
 * The phone's `@/hooks/use-assistant-live`, over the browser's audio and
 * socket. What it reads off the wire is the phone's own reader; the live
 * state (`liveStateOf`) and the transcript (`liveTranscriptStep`) are the
 * shared models both clients read; the level arithmetic (`blockLevels`,
 * `createRoomFloor`, `levelOf`, `writeLevelSteps`, `hushLevel`) and the
 * microphone's one queue (`onMicQueue`) are the phone's, so a call and a
 * dictation never open and close the device under each other.
 *
 * How a surface uses it:
 *
 *   const live = useAssistantLive({
 *     tenantId, level, micLevel,
 *     context: () => ({ threadId, messages, screen, recordId, tz, cards, language, greet: false }),
 *     onTurn: (messages, card, asked) => { append(messages); },
 *     onClosed: (reason) => { ... },       // a LiveEndReason, the server's words, or 'socket 1006'
 *     onError: (code, message) => { ... }, // 'MIC_OFF', 'NO_MIC', 'FAILED', or the server's code
 *   });
 *
 * **Two wires.** Every `start` declares the browser's capabilities
 * (`liveStartContextOf`: version 2, `platform: 'web'`, the duplex it earned
 * (`webDuplexOf`: full on desktop Chrome when its track reads echo cancellation back
 * on, else half), its VAD and attention, the device as "<browser> on <OS>").
 * The server's `open` then picks the handling for the call (`liveWireOf`):
 *
 * - **Version 2** (`open.duplex` half or full). No hold and no waiting: a
 *   frame goes up whenever the person has not muted, except while OS's voice
 *   plays and for `LIVE_ECHO_GRACE_MS` after it (`halfDuplexHeard`). The
 *   state is the shared `liveStateOf` over the server's `state` frames, Mute,
 *   the voice playing and the connection (`look`); the words are the shared
 *   transcript over `line`, `next`, `replaced`, `parked` and `turn`
 *   (`transcript`). Mute is the only thing the person does that stops what
 *   is sent, and nothing unmutes but them. The person's other moves are named
 *   commands: `interrupt` (cut OS's speech now; the mic as it was), `attend`
 *   (a click on the orb: what comes next is for OS), `resumeWork`,
 *   `exitQuiet`, `imHere`, `carryOn`, `continueHere` (take over the call
 *   another device runs) and `stopLive`. `cancel(messageId)` is Next's
 *   Cancel and the working row's stop. Attention goes up as it changes
 *   (`use-live-attention.ts`), and the tab's title says the state.
 *   An unexpected drop reconnects (`reconnectDelayOf`) inside the server's
 *   15 s grace with the `callId` and `token` the `open` gave, holding at most
 *   2 s of the microphone and the typed lines meanwhile; past it, the call
 *   ends `lost`. An `open` with no `state` within `LIVE_STATE_WAIT_MS` is an
 *   older server: the call falls back to the legacy handling.
 * - **The ear** (steps 4.3 and 4.4). The microphone hands over 50 ms frames
 *   (`WEB_FRAME_MS`), joined into the wire's 100 ms. Each runs the shared VAD
 *   (`liveEarOf`): `vad` frames go up, and on a full-duplex call (the gate is
 *   then Mute alone) speech over OS ducks it within 150 ms, except in the
 *   first second of the session's first playback while the canceller
 *   settles; a loud room ducks it and raises the threshold. The
 *   transcriber's `partial` words, while OS plays, first meet the echo
 *   detector (a hit drops the call to half duplex, `duplex { half }`;
 *   earphones on `devicechange` bring it back), then the pause rule
 *   (`pauseOf`: the voice is flushed only when the person took the turn,
 *   with a `playback { paused }` frame). A `playback` frame goes at every
 *   cut and turn end. The track ending or the microphone gone ends the call
 *   (`NO_MIC`); another app taking it (the track mutes) or the context
 *   interrupted (Safari) pauses as Interrupted, and Resume, a click, resumes
 *   the audio (`webAudioStep`).
 * - **Legacy** (any other `open`) keeps today's: the server's `mic` frame
 *   holds the mic while OS works, speaks, or the person's turn went by
 *   (`waiting`), and nothing goes up while it does (`legacyMicHeard`).
 *
 * **Seats** (steps 5.1 and 5.4). `seat: 'setup'` opens the same socket on
 * the setup seat (`?seat=setup` in the query, with no business): the same version-2 call, whose `note` frames (what the
 * note-taker has on each of the person's lines, whether Build can light, a
 * spoken Build) reach `onNote`, and whose app tells the voice of its own
 * progress with `appNote` (a build step, the build done, the proposal).
 * A start whose context says `after: 'create'` carries live mode on after a
 * setup's Create: it plays no rising chime.
 *
 * Both: the screen is kept awake while live mode runs in a visible tab
 * (`live-wake-lock.ts`, J32). `start` asks for the microphone; a refused one
 * is `closed` with `onError('MIC_OFF', …)` and no `onClosed`, a missing one
 * `NO_MIC`, and one that would not start `FAILED`. `stopLive` (Stop live)
 * ends the voice at once and an answer still being worked on lands through
 * `onTurn`, as it does when the server ends the call itself while OS works;
 * `stop` drops it as well. Unmounting ends the call.
 *
 * **Levels.** `micLevel` is the microphone, written for every frame while the
 * person has not muted (the dot grid in the pill: side talk moves only
 * this); `level` is OS's voice, written as each played frame is heard (the
 * orb while it speaks). A legacy call writes the person's heard frames to
 * `level` too, as it always has.
 *
 * OS's work on the call's questions is `live.work` (`call-work.ts`). The
 * cards: the voice's `confirm_card` presses the card on screen through the
 * phone's card registry (`@/lib/agent-card-registry`), the same guarded press
 * a click runs, and the result goes back as `card_result`. A card that takes
 * something away is never pressed by voice. While the conversation is out of
 * sight a yes presses the card all the same (R3), and `onCardHidden` brings
 * the conversation back so the person sees what was saved, but never while
 * the tab itself is hidden.
 * While the socket is open the call is the registry's bridge, so a card
 * changed by hand moves the call's ledger too.
 *
 * The browser's sound has no switch of its own (J24): a tab is muted from the
 * browser.
 *
 * Differences from the phone: no `cookie` (the socket carries the session by
 * itself) and the tenant rides the socket's query (`live-urls.ts`).
 */
import {
  createSayLater,
  LIVE_ECHO_GRACE_MS,
  LIVE_INPUT_RATE,
  LIVE_OUTPUT_RATE,
  LIVE_CLAIM_SHOW_MS,
  LIVE_NAME_HINT_SHOW_MS,
  LIVE_RECONNECT_GRACE_MS,
  lastPersonLineOf,
  personLineWritingOf,
  liveTranscriptStep,
  liveTypingStep,
  NEW_LIVE_TRANSCRIPT,
  NEW_LIVE_TYPING,
  spokenLanguageOf,
  tOr,
  type AssistantLiveContext,
  type AssistantLiveServerMessage,
  type AssistantMicWhy,
  type LiveConnection,
  type LiveDuplexMode,
  type LiveClientFrame,
  type LiveLook,
  type LiveSeat,
  type LiveServerState,
  type LiveStateName,
  type LiveTranscript,
  type LiveTyping,
  type SayLater,
  echoSwitchStep,
  duckSettlingOf,
  liveEarOf,
  NEW_ECHO_SWITCH,
  NEW_LIVE_EAR,
  NEW_OS_TURN,
  NEW_PRE_ROLL,
  osTurnStep,
  osTurnWordsOf,
  pauseOf,
  preRollPush,
  preRollTake,
  type EarBlock,
  type EchoSwitch,
  type LiveEarModel,
  type OsTurn,
  type PreRoll,
} from '@alluzo/shared';
import type { UIMessage } from 'ai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useLiveSensitivityStore } from '~/stores/live-sensitivity-store';

import { actOnCard, cardHandleOf, clearRing, noteDrawnInCall, onCardsChanged, pendingCards, ringCard, setCardCallBridge } from '@/lib/agent-card-registry';
import { proposalCallIdsOf } from '@/lib/agent-proposals';
import { isVoiceFold, NEW_HEARD, trackHeard, turnWords, type HeardEvent } from '@/lib/assistant-live-wire';
import { createPcmJoiner } from '@/lib/live-pcm';
import { onMicQueue } from '@/lib/mic-queue';
import { hushLevel, writeLevelSteps } from '@/lib/orb-level';
import { blockLevels, createRoomFloor, levelOf, SPEAKER_FLOOR_DB } from '@/lib/voice-level';

import { callWorkAfter, NO_CALL_WORK, type CallWork } from './call-work';
import type { VoiceLevel } from './level';
import { createLiveAudio, type LiveAudio } from './live-audio';
import { earconStep, LIVE_END_SOUND, webLookOf } from './live-edges';
import { NEW_WEB_AUDIO, WEB_FRAME_MS, WEB_PRE_ROLL_MS, webAudioStep, webDuplexOf, webEarphonesOf, type WebAudioEvent, type WebAudioState } from './live-ear';
import { hasSeen, markSeen } from './live-first-run';
import { liveHearingOf } from './live-words';
import {
  createAudioRing,
  deviceNameOf,
  halfDuplexHeard,
  LIVE_COMMAND_FRAMES,
  isDrop,
  legacyMicHeard,
  legacyTurnOf,
  LIVE_STATE_WAIT_MS,
  liveStartContextOf,
  liveWireOf,
  reconnectDelayOf,
  turnOfLook,
  type LiveCommand,
  type LiveTurn,
  type LiveWire,
} from './live-mode';
import { connectAssistantLive, type WebAssistantLiveSocket } from './live-socket';
import { liveViewCardsNow, onLiveViewCardsChanged } from './live-view-cards';
import { createLiveWakeLock } from './live-wake-lock';
import { micSentence } from './mic-words';
import { micFailureOf } from './microphone';
import { frameSlot } from './pcm-player';
import { useLiveAttention, type LiveAttentionFrame } from './use-live-attention';

/** How long the earphones note shows once they brought full duplex back. */
export const EARPHONES_NOTE_MS = 4_000;

/**
 * Where the call is. `idle` is before the first call and after the person
 * ended one; `closed` is a call that ended by itself, or never got its mic.
 * Between the two, whose turn it is (`live-mode.ts`, `LiveTurn`); on a
 * version-2 call it is read off the shared look (`turnOfLook`), and never
 * `waiting`.
 */
export type AssistantLiveState = 'idle' | 'connecting' | LiveTurn | 'closed';

/** Another device runs the person's live call: its name, and how long it has been live. */
export type LiveElsewhere = { readonly device: string | null; readonly sinceMs: number };

/** A setup seat's note on one of the person's lines (`note` frame). */
export type LiveSeatNote = Extract<AssistantLiveServerMessage, { readonly type: 'note' }>;

/**
 * The studio seat's frames (a call whose start carried `setupSession`): the
 * drafter's parts still drawing, its work, its turn ended, OS pointing at a
 * card, the open question wanting typing, Build by voice, Open by voice.
 */
export type StudioLiveFrame = Extract<
  AssistantLiveServerMessage,
  { readonly type: 'drafting' | 'work' | 'drafted' | 'point' | 'ask_hint' | 'build_sheet' | 'open_workspace' }
>;

/** What the setup app tells the voice of its own progress (`app_note`). */
export type LiveAppNote = Extract<LiveClientFrame, { readonly type: 'app_note' }>['kind'];

export type AssistantLiveStop = {
  /**
   * No falling chime: live mode is not ending, the call is moving seats
   * (a setup's Create carries it on into the new business).
   */
  readonly quiet?: boolean;
};

export type AssistantLiveStart = {
  /** Take over the live call another device runs ("Continue live here"). */
  readonly takeover?: boolean;
};

export type AssistantLiveInput = {
  /** The business the call is about. A start without one does nothing, except on the setup seat, which has none. */
  readonly tenantId: string | null;
  /** The setup seat the call opens on; absent is the assistant. */
  readonly seat?: Exclude<LiveSeat, 'assistant'>;
  /** A setup seat's note on one of the person's lines. */
  readonly onNote?: (note: LiveSeatNote) => void;
  /** The studio seat's frames, as they come. */
  readonly onStudioFrame?: (frame: StudioLiveFrame) => void;
  /** OS's voice, 0..1, as each played frame is heard: the orb. This hook is its only writer while a call is up. */
  readonly level: VoiceLevel;
  /** The microphone, 0..1, for every frame while not muted: the dot grid. Absent, it is not written. */
  readonly micLevel?: VoiceLevel;
  /** What the call knows, read at the moment it starts. */
  readonly context: () => AssistantLiveContext;
  /**
   * One finished exchange, already kept on the server: append it. `card`: a
   * card is waiting to be confirmed. `asked`: the voice took the question to
   * Coach, so the answer is Coach's.
   */
  readonly onTurn: (messages: UIMessage[], card: boolean, asked: boolean) => void;
  /** The call ended by itself: the server's reason, or `socket <code>`. */
  readonly onClosed: (reason: string) => void;
  /**
   * `MIC_OFF` (refused), `NO_MIC` (this browser has none; the phone always
   * does), `FAILED` (it would not start), each with this browser's sentence;
   * or the server's own code and sentence.
   */
  readonly onError: (code: string, message: string) => void;
  /**
   * Whether the conversation, and so its cards, is in front of the person
   * now. Absent, it always is. A spoken yes while it is not presses nothing.
   */
  readonly conversationShown?: () => boolean;
  /** A spoken yes came while the conversation was covered: bring it back, so the card can be looked at. */
  readonly onCardHidden?: () => void;
  /** OS is minimised: the person is not looking at it, whatever the tab. */
  readonly minimised?: boolean;
};

export type AssistantLive = {
  readonly state: AssistantLiveState;
  /** Which handling the call runs, once the server said `open`; null before. */
  readonly wire: LiveWire | null;
  /**
   * Live mode's look (the shared model) while a call is up: a version-2
   * call's from the server's state; before `open` and on a legacy call, from
   * the call's own turn. Null with no call.
   */
  readonly look: LiveLook | null;
  /** The live transcript of a version-2 call: the rows keyed by the server's ids, the one Next, parked and replaced. */
  readonly transcript: LiveTranscript;
  /** The `elsewhere` the server sent: another device runs the call. Kept after the call ends, until the next start. */
  readonly elsewhere: LiveElsewhere | null;
  /** When the call opened (`Date.now()`), for the divider's time; null before. */
  readonly since: number | null;
  /** The version-2 call's id from its `open`, kept until the next start: the thread keeps its divider under `${callId}-end`. Null on a legacy call. */
  readonly callId: string | null;
  /**
   * The person's words in the turn being spoken, whole, replaced as they
   * grow; null between turns. On a version-2 call, their partial line in the
   * transcript (`liveHearingOf`).
   */
  readonly hearing: string | null;
  /** Legacy: the last finished turn's words. */
  readonly heard: string | null;
  /** Legacy: what the voice is saying, as it arrives, or last said. */
  readonly caption: string | null;
  /** A call is up, or coming up, or reconnecting. */
  readonly active: boolean;
  /** A call that ended for the conversation still owes it an answer (`stopLive`). */
  readonly owed: boolean;
  readonly start: (options?: AssistantLiveStart) => void;
  /** Ends the call, and drops anything the assistant was still answering. Idempotent. */
  readonly stop: (options?: AssistantLiveStop) => void;
  /** Stop live: the voice ends now; an answer still owed lands through `onTurn`. Answers how long live mode ran, in ms. */
  readonly stopLive: () => number;
  /** The person's own Mute, in every state: mute or unmute, and nothing else. */
  readonly toggleMute: () => void;
  /** Cut OS's speech now (the shipped Interrupt). The mic stays as the person left it. */
  readonly interrupt: () => void;
  /** A click on the orb: what comes next is for OS. Never an unmute. */
  readonly attend: () => void;
  /** Bring back the request a spoken stop parked. */
  readonly resumeWork: () => void;
  /** Leave Quiet. */
  readonly exitQuiet: () => void;
  /** I'm here: Still there? is answered. */
  readonly imHere: () => void;
  /** The utterance the server's `unsure` named a moment ago: the dimmed "Was that for me?" chip shows (option B, step 5). */
  readonly claimable: string | null;
  /**
   * The typing dots on the person's side of the thread (`liveTypingStep`):
   * this browser's ear hears them speaking while Listening, and no line of
   * theirs has come for it yet. Never words.
   */
  readonly typing: boolean;
  /** The chip clicked: the utterance it named was for OS. */
  readonly claim: () => void;
  /** Carry on past the 30-minute mark. */
  readonly carryOn: () => void;
  /** Continue live here: take over the call another device runs. */
  readonly continueHere: () => void;
  /** Stop a request: the running one, or the waiting Next by its id. */
  readonly cancel: (messageId?: string) => void;
  /**
   * The shipped live button, until the long pill: on a legacy call it cuts
   * the voice off, unmutes and opens the mic (`interrupt`); on a version-2
   * call it interrupts OS while it speaks, else attends.
   */
  readonly talk: () => void;
  /** Whether the voice is still playing, read now. */
  readonly isTalking: () => boolean;
  /** OS's work on the call's questions (`call-work.ts`). */
  readonly work: CallWork;
  /**
   * A line for OS that did not come by voice. Answers whether it went out, or
   * waits for a reconnect; a line that waited and the call ended before it was
   * back is handed to `unsent`, so it is sent the typed way rather than lost.
   */
  readonly say: (text: string, unsent?: () => void) => boolean;
  /** Files added and read, by id: OS is told what they say (Decided 19 of docs/plans/setup-from-files.md; on the setup seat, Decided 9). */
  readonly files: (fileIds: readonly string[]) => void;
  /** Where the person is now, and what the page shows: the next question reads it, and nothing is said. */
  readonly place: (next: Pick<AssistantLiveContext, 'screen' | 'recordId' | 'cards' | 'view'>) => void;
  /** A setup seat: the app's own progress for the voice (a build step, the build done, the proposal's lines). Answers whether it went out. */
  readonly appNote: (kind: LiveAppNote, text: string) => boolean;
  /** The studio seat: the canvas selection changed (card keys; `[]` cleared). Answers whether it went out. */
  readonly scope: (keys: readonly string[]) => boolean;
};

/** The browser's name for itself, read once. */
const deviceNow = (): string => deviceNameOf(typeof navigator === 'undefined' ? '' : navigator.userAgent);

const tabVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** The cards a yes could save that stand whole on screen now, by tool call, then the page's own parts in view (the studio's canvas). */
const cardsInView = async (): Promise<readonly string[]> => {
  const whole = await Promise.all(
    pendingCards().map(async ({ toolCallId, handle }) => ((await handle.isWholeOnScreen().catch(() => false)) ? toolCallId : null)),
  );
  return [...whole.filter((id): id is string => id !== null), ...liveViewCardsNow()];
};

/** Hear a card that comes or goes, and a change of the page's parts in view. */
const onViewChanged = (listener: () => void): (() => void) => {
  const cards = onCardsChanged(listener);
  const parts = onLiveViewCardsChanged(listener);
  return () => {
    cards();
    parts();
  };
};

/** A dropped call coming back: when it dropped, how many tries so far, and the next one's timer. */
type Reconnecting = { readonly droppedAt: number; attempt: number; timer: ReturnType<typeof setTimeout> | null; deadline: ReturnType<typeof setTimeout> };

export const useAssistantLive = (input: AssistantLiveInput): AssistantLive => {
  const called = useRef(input);
  useEffect(() => {
    called.current = input;
  });

  const [state, setState] = useState<AssistantLiveState>('idle');
  const [hearing, setHearing] = useState<string | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [caption, setCaption] = useState<string | null>(null);
  const [work, setWork] = useState<CallWork>(NO_CALL_WORK);
  // The version-2 look's inputs, as React state so the look re-renders with them.
  const [wire, setWire] = useState<LiveWire | null>(null);
  const [duplex, setDuplex] = useState<LiveDuplexMode | null>(null);
  const [connection, setConnection] = useState<LiveConnection>('closed');
  const [server, setServer] = useState<LiveServerState | null>(null);
  const [endsInMs, setEndsInMs] = useState<number | undefined>(undefined);
  /** The server's last `state` said the conversation window is open (option B, 7 Oct 2026): the orb's open ring. */
  const [windowOpen, setWindowOpen] = useState(false);
  const [mutedShown, setMutedShown] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [transcript, setTranscript] = useState<LiveTranscript>(NEW_LIVE_TRANSCRIPT);
  /** The call's language setting, as its start said it: the save words fall back to it (7 Oct, item 4). */
  const [callLanguage, setCallLanguage] = useState<string | null>(null);
  const [elsewhere, setElsewhere] = useState<LiveElsewhere | null>(null);
  /** The server's `name_hint`: Listening says how to be heard for `LIVE_NAME_HINT_SHOW_MS` (7 Oct evening, item 4). */
  const [nameHint, setNameHint] = useState(false);
  const nameHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The server's `unsure`: the "Was that for me?" chip names this utterance for `LIVE_CLAIM_SHOW_MS` (option B, step 5). */
  const [claimable, setClaimable] = useState<string | null>(null);
  const claimTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [since, setSince] = useState<number | null>(null);
  /** The "Tap to mute" tip over Mute: until this browser mutes or stops live mode once (`muteTipSeen`). */
  const [muteTip, setMuteTip] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);

  const audio = useRef<LiveAudio | null>(null);
  const socket = useRef<WebAssistantLiveSocket | null>(null);
  const muted = useRef(false);
  /** Which handling the call runs, read by the microphone's frames. */
  const wireNow = useRef<LiveWire | null>(null);
  /** The call's resume, from a version-2 `open`. */
  const resumable = useRef<{ readonly callId: string; readonly token: string } | null>(null);
  /** A dropped version-2 call coming back; null while connected. */
  const reconnecting = useRef<Reconnecting | null>(null);
  /** The microphone's last 2 s while reconnecting, sent first once back. */
  const ring = useRef(createAudioRing());
  /** Lines typed while reconnecting, sent in order once back; an ending first answers each not sent. */
  const sayLater = useRef<SayLater>(createSayLater());
  /**
   * The newest attention frame, sent or held back while reconnecting: the
   * attention hook offers each change once, so the re-attach sends it.
   */
  const attentionNow = useRef<LiveAttentionFrame | null>(null);
  /** An `open` waiting for its first `state`: an older server never sends one. */
  const stateWait = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Legacy: live mode's hold on the mic, as the server last said it; null before it has. */
  const hold = useRef<AssistantMicWhy | null>(null);
  /** The person cut the voice in: its frames still on their way are dropped until the server says it stopped. */
  const cutting = useRef(false);
  /** The assistant has the question and has not answered it: between `working` and its `turn`. */
  const working = useRef(false);
  /** The question OS answers now, by the id the thread keeps it under: a turn that does not hold it is the voice's own small talk. */
  const askingNow = useRef<string | null>(null);
  /** Takes this call off the card registry's bridge. */
  const unbridge = useRef<(() => void) | null>(null);
  const heardTrack = useRef(NEW_HEARD);
  const saidParts = useRef('');
  const saidFresh = useRef(true);
  const era = useRef(0);
  const running = useRef(false);
  /** When the call opened, for the divider's time. */
  const openedAt = useRef<number | null>(null);
  /** Calls that ended for the conversation and still owe it an answer. */
  const owing = useRef(new Set<WebAssistantLiveSocket>());
  const [owed, setOwed] = useState(false);
  const quietUntil = useRef(0);
  const room = useRef(createRoomFloor());
  /** Where the voice's next frame will be heard, on `Date.now()`'s clock; 0 is an empty pipe. */
  const playhead = useRef(0);
  /** The played frames' levels still waiting for their moment. */
  const pending = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [wakeLock] = useState(() => createLiveWakeLock());
  /** The call's duplex from `open`, read where a frame or a word arrives. */
  const duplexNow = useRef<LiveDuplexMode | null>(null);
  /** The echo switch (step 4.3): a full call its echo dropped to half, and the earphones note once they bring it back. */
  const [echoShown, setEchoShown] = useState<EchoSwitch>(NEW_ECHO_SWITCH);
  const echoNow = useRef<EchoSwitch>(NEW_ECHO_SWITCH);
  const earphonesNote = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** OS's turn as it plays here: what the echo detector reads the transcriber's words against. */
  const osTurn = useRef<OsTurn>(NEW_OS_TURN);
  /** The browser's ear (step 4.4): its VAD, the newest frames, the 100 ms wire frames joined from 50 ms ones. */
  const earModel = useRef<LiveEarModel>(NEW_LIVE_EAR);
  /**
   * The typing dots (12 Oct): their model, stepped on every microphone frame
   * and on what moves it (the state, a line, a `passed`), with what it reads
   * that only renders know (the look's state, the person's newest line).
   */
  const [typing, setTyping] = useState(false);
  const typingModel = useRef<LiveTyping>(NEW_LIVE_TYPING);
  const typingState = useRef<LiveStateName | null>(null);
  const typingLine = useRef<string | null>(null);
  const typingWriting = useRef(false);
  const passedAt = useRef<number | null>(null);
  const stepTyping = useCallback((now: number) => {
    const next = liveTypingStep(typingModel.current, {
      now,
      speaking: earModel.current.sent?.speaking === true,
      state: typingState.current,
      lastLine: typingLine.current,
      transcribing: typingWriting.current,
      passedAt: passedAt.current,
    });
    typingModel.current = next;
    setTyping(next.shown);
  }, []);
  const preRoll = useRef<PreRoll>(NEW_PRE_ROLL);
  const joiner = useRef(createPcmJoiner());
  /** When the session's first playback began: the canceller settles for a second, and nothing ducks then. */
  const firstPlaybackAt = useRef<number | null>(null);
  /** OS ducked under the person's voice, the room loud, and the audio held by another app or the system. */
  const [ducked, setDucked] = useState(false);
  const [loud, setLoud] = useState(false);
  const [interrupted, setInterrupted] = useState(false);
  const webAudio = useRef<WebAudioState>(NEW_WEB_AUDIO);
  /** The call that is up, ended for the microphone gone. */
  const endForNoMic = useRef<(() => void) | null>(null);
  /** This call carries live mode on after a setup's Create (`after: 'create'`): it plays no rising chime. */
  const carried = useRef(false);

  /**
   * The echo switch moved: the server is told (`duplex`), the look follows,
   * and the earphones note goes after `EARPHONES_NOTE_MS`.
   */
  const echoSwitch = useCallback((next: EchoSwitch, send: 'half' | 'full'): void => {
    echoNow.current = next;
    setEchoShown(next);
    socket.current?.frame({ type: 'duplex', mode: send });
    if (earphonesNote.current !== null) clearTimeout(earphonesNote.current);
    earphonesNote.current = null;
    if (send !== 'full') return;
    const mine = era.current;
    earphonesNote.current = setTimeout(() => {
      earphonesNote.current = null;
      if (era.current !== mine) return;
      const done = echoSwitchStep(echoNow.current, { type: 'note_done' }).switch;
      echoNow.current = done;
      setEchoShown(done);
    }, EARPHONES_NOTE_MS);
  }, []);

  /**
   * The browser's audio told something (`webAudioStep`): another app took the
   * microphone or the system interrupted the audio, so the call pauses as
   * Interrupted (the voice stops, nothing is sent, the server is told) until
   * the person's Resume; the microphone gone ends the call `no_mic`;
   * earphones coming in bring a call its echo dropped back to full.
   */
  const audioEvent = useCallback((event: WebAudioEvent): void => {
    const next = webAudioStep(webAudio.current, event);
    webAudio.current = next.state;
    switch (next.action) {
      case 'pause': {
        setInterrupted(true);
        const made = audio.current;
        if (made !== null && made.isSpeaking()) {
          socket.current?.frame({ type: 'playback', playedMs: made.playedMs(), state: 'idle' });
          cutting.current = true;
          made.flush();
        }
        socket.current?.frame({ type: 'pause', why: 'system' });
        return;
      }
      case 'resume':
        setInterrupted(false);
        socket.current?.frame({ type: 'resume' });
        return;
      case 'no_mic':
        endForNoMic.current?.();
        return;
      case 'earphones_in': {
        const switched = echoSwitchStep(echoNow.current, { type: 'earphones', on: true, openFull: duplexNow.current === 'full' });
        if (switched.send !== null) echoSwitch(switched.switch, switched.send);
        return;
      }
      case null:
        return;
    }
  }, [echoSwitch]);

  /** Legacy: whose turn it is now, from the person's Mute, the voice, the server's hold and OS's work. */
  const waiting = useCallback(
    (playingNow = audio.current?.isSpeaking() ?? false): AssistantLiveState =>
      legacyTurnOf({ muted: muted.current, playing: playingNow, hold: hold.current, working: working.current }),
    [],
  );
  /** A state a live call can be in. Nothing may resurrect one that has ended. */
  const settle = useCallback(
    (next: AssistantLiveState): void =>
      setState((held) => (held === 'connecting' || held === 'closed' || held === 'idle' ? held : next)),
    [],
  );

  /** Drop every played frame's level not yet written, and bring both levels to rest. */
  const hush = useCallback((): void => {
    for (const timer of pending.current) clearTimeout(timer);
    pending.current.clear();
    playhead.current = 0;
    hushLevel(called.current.level);
    const mic = called.current.micLevel;
    if (mic !== undefined) hushLevel(mic);
  }, []);

  /** The call no longer answers for the cards: no ring stays on, and a press by hand reaches no call. */
  const letCardsGo = useCallback((): void => {
    unbridge.current?.();
    unbridge.current = null;
    clearRing();
  }, []);

  /** Every timer and buffer a version-2 call keeps, let go. */
  const letWireGo = useCallback((): void => {
    const back = reconnecting.current;
    if (back !== null) {
      if (back.timer !== null) clearTimeout(back.timer);
      clearTimeout(back.deadline);
    }
    reconnecting.current = null;
    if (stateWait.current !== null) clearTimeout(stateWait.current);
    stateWait.current = null;
    ring.current.drain();
    sayLater.current.drop();
    resumable.current = null;
  }, []);

  /**
   * Keep a socket for what it still owes: only its turns are read, and each
   * lands through `onTurn`. `sayStop: false` when the server has already
   * ended the call (its `closed`), so no `stop` goes.
   */
  const owe = useCallback((held: WebAssistantLiveSocket, sayStop: boolean): void => {
    owing.current.add(held);
    setOwed(true);
    held.handOff(
      (message) => called.current.onTurn([...message.messages] as UIMessage[], message.card, true),
      () => {
        owing.current.delete(held);
        setOwed(owing.current.size > 0);
      },
      { sayStop },
    );
  }, []);

  const start = useCallback(
    (options?: AssistantLiveStart) => {
      const { tenantId, seat } = called.current;
      if (running.current || (tenantId === null && seat !== 'setup')) return;
      running.current = true;
      const mine = era.current + 1;
      era.current = mine;
      setState('connecting');
      setConnection('connecting');
      setWire(null);
      setDuplex(null);
      duplexNow.current = null;
      echoNow.current = NEW_ECHO_SWITCH;
      setEchoShown(NEW_ECHO_SWITCH);
      if (earphonesNote.current !== null) clearTimeout(earphonesNote.current);
      earphonesNote.current = null;
      osTurn.current = NEW_OS_TURN;
      earModel.current = NEW_LIVE_EAR;
      typingModel.current = NEW_LIVE_TYPING;
      passedAt.current = null;
      setTyping(false);
      preRoll.current = NEW_PRE_ROLL;
      joiner.current.drop();
      firstPlaybackAt.current = null;
      webAudio.current = NEW_WEB_AUDIO;
      setDucked(false);
      setLoud(false);
      setInterrupted(false);
      setServer(null);
      setEndsInMs(undefined);
      setWindowOpen(false);
      setPlaying(false);
      setMutedShown(false);
      setTranscript(NEW_LIVE_TRANSCRIPT);
      setElsewhere(null);
      setSince(null);
      setCallId(null);
      setMuteTip(!hasSeen('muteTipSeen'));
      setHearing(null);
      setHeard(null);
      setCaption(null);
      // A call opens listening, whatever the last one was left as.
      muted.current = false;
      wireNow.current = null;
      openedAt.current = null;
      attentionNow.current = null;
      letWireGo();
      hold.current = null;
      cutting.current = false;
      working.current = false;
      askingNow.current = null;
      setWork(NO_CALL_WORK);
      heardTrack.current = NEW_HEARD;
      saidParts.current = '';
      saidFresh.current = true;
      quietUntil.current = 0;
      room.current.reset();
      hush();
      wakeLock.start();

      const made = audio.current ?? createLiveAudio();
      audio.current = made;
      made.setVolume(1);

      const finish = (reason: string): void => {
        if (era.current !== mine) return;
        era.current = mine + 1;
        endForNoMic.current = null;
        // Live mode that ran ends on the falling chime, played out before the speaker goes.
        if (openedAt.current !== null) made.earcon(LIVE_END_SOUND);
        running.current = false;
        working.current = false;
        askingNow.current = null;
        letWireGo();
        wakeLock.stop();
        const held = socket.current;
        socket.current = null;
        held?.close();
        letCardsGo();
        void onMicQueue(() => made.stop()).catch(() => undefined);
        hush();
        setHearing(null);
        setWork(NO_CALL_WORK);
        setPlaying(false);
        setConnection('closed');
        setState('closed');
        called.current.onClosed(reason);
      };

      /** The microphone's frame, as a level: four blocks against the room. */
      const micSteps = (pcm: Uint8Array) =>
        blockLevels(pcm, LIVE_INPUT_RATE).map((block) => ({
          value: levelOf(block.db, room.current.feed(block.db)),
          ms: block.ms,
        }));

      /** A frame of the voice, as the orb's level at the moment it will be heard. */
      const speakLevel = (pcm: Uint8Array): void => {
        const blocks = blockLevels(pcm, LIVE_OUTPUT_RATE);
        if (blocks.length === 0) return;
        const now = Date.now();
        const slot = frameSlot(
          now,
          playhead.current,
          blocks.reduce((sum, block) => sum + block.ms, 0),
        );
        playhead.current = slot.playheadMs;
        const steps = blocks.map((block) => ({ value: levelOf(block.db, SPEAKER_FLOOR_DB), ms: block.ms }));
        const timer = setTimeout(() => {
          pending.current.delete(timer);
          if (era.current === mine) writeLevelSteps(called.current.level, steps);
        }, slot.atMs - now);
        pending.current.add(timer);
      };

      const hear = (event: HeardEvent): void => {
        const next = trackHeard(heardTrack.current, event);
        heardTrack.current = next.track;
        if (next.hearing !== undefined) setHearing(next.hearing);
        if (next.heard !== undefined) setHeard(next.heard);
      };

      /** One of OS's frames about its work, into what the conversation draws. */
      const follow = (message: AssistantLiveServerMessage, fold = false): void => {
        const now = { fold, working: working.current };
        setWork((held) => callWorkAfter(held, message, now));
      };

      /** The call is back after a drop: what the microphone held goes first, then the Mute and the attention as they are now, then the lines typed meanwhile. */
      const reattached = (now: WebAssistantLiveSocket): void => {
        const back = reconnecting.current;
        if (back === null) return;
        if (back.timer !== null) clearTimeout(back.timer);
        clearTimeout(back.deadline);
        reconnecting.current = null;
        for (const pcm of ring.current.drain()) now.sendAudio(pcm);
        now.mute(muted.current);
        // Attention that changed while away was never sent, and is not offered again.
        const attention = attentionNow.current;
        if (attention !== null) now.frame(attention);
        sayLater.current.send((text) => now.say(text));
      };

      /** Everything only a version-2 call reads. */
      const receiveWire = (message: AssistantLiveServerMessage): boolean => {
        switch (message.type) {
          case 'state':
            if (stateWait.current !== null) clearTimeout(stateWait.current);
            stateWait.current = null;
            setServer(message.state);
            setEndsInMs(message.endsInMs);
            setWindowOpen(message.window?.open === true);
            return true;
          case 'next':
            // The one visible Next is the transcript's, and the shipped thread draws it as the queued question.
            setTranscript((held) => liveTranscriptStep(held, message));
            follow(message);
            return true;
          case 'line':
            if (message.role === 'assistant') osTurn.current = osTurnStep(osTurn.current, { type: 'line', id: message.id, text: message.text, atMs: Date.now() });
            setTranscript((held) => liveTranscriptStep(held, message));
            return true;
          case 'replaced':
          case 'parked':
            setTranscript((held) => liveTranscriptStep(held, message));
            return true;
          case 'partial': {
            // A full-duplex call only, while OS plays: OS's own voice coming back drops the call to half. Never drawn or kept.
            if (duplexNow.current !== 'full' || cutting.current || !made.isSpeaking()) return true;
            const switched = echoSwitchStep(echoNow.current, {
              type: 'partial',
              text: message.text,
              final: message.final,
              osWords: osTurnWordsOf(osTurn.current, Date.now()),
              playing: true,
              openFull: true,
            });
            if (switched.send !== null) {
              echoSwitch(switched.switch, switched.send);
              return true;
            }
            // Otherwise the person's words may pause it (a stop, a yes/no, two words not OS's own): flushed only now.
            if (!echoNow.current.echo && pauseOf(message.text, osTurnWordsOf(osTurn.current, Date.now()) ?? '', { loud: earModel.current.ear.loud })) {
              const playedMs = made.playedMs();
              cutting.current = true;
              made.flush();
              hush();
              saidFresh.current = true;
              socket.current?.frame({ type: 'playback', playedMs, state: 'paused' });
            }
            return true;
          }
          case 'elsewhere':
            setElsewhere({ device: message.device ?? null, sinceMs: message.sinceMs });
            return true;
          case 'unsure':
            // Its id only: no words of what was said are ever shown.
            if (claimTimer.current !== null) clearTimeout(claimTimer.current);
            setClaimable(message.utteranceId);
            claimTimer.current = setTimeout(() => {
              claimTimer.current = null;
              setClaimable(null);
            }, LIVE_CLAIM_SHOW_MS);
            return true;
          case 'passed':
            // An utterance settled with no line: the typing dots for it go, after their grace.
            passedAt.current = Date.now();
            stepTyping(Date.now());
            return true;
          case 'name_hint':
            if (nameHintTimer.current !== null) clearTimeout(nameHintTimer.current);
            setNameHint(true);
            nameHintTimer.current = setTimeout(() => {
              nameHintTimer.current = null;
              setNameHint(false);
            }, LIVE_NAME_HINT_SHOW_MS);
            return true;
          case 'note':
            called.current.onNote?.(message);
            return true;
          case 'card_voice':
            return true;
          case 'drafting':
          case 'work':
          case 'drafted':
          case 'point':
          case 'ask_hint':
          case 'build_sheet':
          case 'open_workspace':
            called.current.onStudioFrame?.(message);
            return true;
          default:
            return false;
        }
      };

      const receive = (message: AssistantLiveServerMessage, from: WebAssistantLiveSocket): void => {
        if (receiveWire(message)) return;
        switch (message.type) {
          case 'open': {
            const next = liveWireOf(message);
            if (reconnecting.current !== null) {
              // Back inside the grace: the same call, the same session; no greeting.
              setConnection('open');
              reattached(from);
              return;
            }
            wireNow.current = next;
            setWire(next);
            setDuplex(message.duplex ?? null);
            duplexNow.current = message.duplex ?? null;
            setConnection('open');
            openedAt.current = Date.now();
            setSince(openedAt.current);
            if (next === 'v2') {
              resumable.current = message.callId !== undefined && message.token !== undefined ? { callId: message.callId, token: message.token } : null;
              setCallId(message.callId ?? null);
              setState('listening');
              // An older server names a duplex and never says its state: today's handling, for this call.
              // One wait at a time: a second `open` must not leave the first's running.
              if (stateWait.current !== null) clearTimeout(stateWait.current);
              stateWait.current = setTimeout(() => {
                stateWait.current = null;
                if (era.current !== mine || wireNow.current !== 'v2') return;
                wireNow.current = 'legacy';
                resumable.current = null;
                setWire('legacy');
                setState(waiting());
              }, LIVE_STATE_WAIT_MS);
              return;
            }
            setState(waiting());
            return;
          }
          case 'speaking':
            // A new turn of the voice's: nothing of it was cut, and what is heard of it counts from here.
            cutting.current = false;
            made.newLine();
            if (wireNow.current !== 'v2') setState(waiting(true));
            return;
          case 'interrupted':
            cutting.current = false;
            made.flush();
            hush();
            saidFresh.current = true;
            if (wireNow.current !== 'v2') settle(waiting());
            return;
          case 'mic':
            // Legacy: live mode's hold, the one source for the waiting state.
            if (wireNow.current === 'v2') return;
            hold.current = message.why;
            cutting.current = false;
            settle(waiting());
            return;
          case 'hearing':
          case 'heard':
            hear(message);
            return;
          case 'said': {
            if (saidFresh.current) {
              saidParts.current = '';
              saidFresh.current = false;
            }
            if (!message.final) {
              saidParts.current += message.text;
              const now = saidParts.current.trim();
              if (now !== '') setCaption(now);
              return;
            }
            const words = turnWords(saidParts.current, message.text);
            if (words !== '') setCaption(words);
            saidFresh.current = true;
            return;
          }
          case 'asking':
            follow(message);
            if (message.queued) return;
            working.current = true;
            askingNow.current = message.messageId;
            // The question is what the call shows while it is worked on.
            hear({ type: 'working' });
            if (!made.isSpeaking()) settle(waiting());
            return;
          case 'progress':
            follow(message);
            return;
          case 'not_reached':
            // The slip is the voice's own. OS still on a question goes on.
            follow(message);
            if (!working.current && !made.isSpeaking()) settle(waiting());
            return;
          case 'working':
            working.current = true;
            hear({ type: 'working' });
            if (!made.isSpeaking()) settle(waiting());
            return;
          case 'turn': {
            // The cards this turn draws are this call's own, which the voice
            // counts apart from any drawn before it, and so does the read-back.
            const messages = [...message.messages] as UIMessage[];
            noteDrawnInCall(proposalCallIdsOf(messages));
            setTranscript((held) => liveTranscriptStep(held, { type: 'turn', messages }));
            // Small talk the voice answered by itself while OS works on another
            // question: kept in the thread, and OS's work goes on.
            if (isVoiceFold(askingNow.current, message)) {
              follow(message, true);
              hear({ type: 'turn' });
              saidFresh.current = true;
              called.current.onTurn(messages, message.card, false);
              if (!made.isSpeaking()) settle(waiting());
              return;
            }
            const asked = working.current;
            working.current = false;
            askingNow.current = null;
            follow(message);
            hear({ type: 'turn' });
            saidFresh.current = true;
            called.current.onTurn(messages, message.card, asked);
            if (!made.isSpeaking()) settle(waiting());
            return;
          }
          case 'confirm_card': {
            const { toolCallId, action } = message;
            // A yes on a card the person cannot see presses it all the same
            // (R3): the conversation comes back so they see what was saved,
            // unless the tab itself is hidden: nothing opens behind it.
            if (action === 'confirm' && called.current.conversationShown?.() === false && tabVisible()) called.current.onCardHidden?.();
            // The card a yes lands on wears the ring while it saves, or while its
            // click confirmation is up. Pressed as a click would press it, and
            // answered to this call only: a call that ended while the card saved
            // is owed nothing.
            const handle = cardHandleOf(toolCallId);
            void (async () => {
              if (action === 'confirm' && handle !== null && (await handle.isWholeOnScreen())) ringCard(toolCallId, { row: null });
              const result = await actOnCard({ toolCallId, action });
              if (era.current === mine) socket.current?.cardResult({ toolCallId, ...result });
            })();
            return;
          }
          case 'error':
            // A failed question must not leave the orb thinking forever. Every
            // code the server sends ends the call; the close that follows moves
            // the surface to `closed`.
            working.current = false;
            if (!made.isSpeaking()) settle(waiting());
            called.current.onError(message.code, message.message);
            return;
          case 'closed': {
            // The server drains running work after `closed` and sends its
            // turn on this socket: a call still working keeps it, so the
            // answer in flight lands as text (C2).
            const held = socket.current;
            if (working.current && held !== null) {
              socket.current = null;
              owe(held, false);
            }
            finish(message.reason);
            return;
          }
        }
      };

      made.onSpeakingChange((speaking) => {
        if (era.current !== mine) return;
        osTurn.current = osTurnStep(osTurn.current, { type: 'playing', on: speaking, atMs: Date.now() });
        setPlaying(speaking);
        if (!speaking) {
          quietUntil.current = Date.now() + LIVE_ECHO_GRACE_MS;
          hush();
          // Speech the ear hears from here on is the person's, not the voice's tail; nothing is left to duck.
          earModel.current = { ...earModel.current, quietAtMs: earModel.current.ear.clockMs };
          made.unduck();
          setDucked(false);
          // The turn's end, or its cut: how much of it was heard.
          if (wireNow.current === 'v2') socket.current?.frame({ type: 'playback', playedMs: made.playedMs(), state: 'idle' });
        }
        if (wireNow.current !== 'v2') settle(waiting(speaking));
      });

      const base = called.current.context();
      // Live mode carried on after a setup's Create rather than started: no rising chime.
      carried.current = base.after === 'create';
      setCallLanguage(base.language ?? null);
      const device = deviceNow();

      /** The card registry's bridge, on whichever socket the call has now. */
      const bridge = (): void => {
        unbridge.current?.();
        unbridge.current = setCardCallBridge({
          report: (toolCallId, result) => {
            if (era.current === mine) socket.current?.cardResult({ toolCallId, ...result });
          },
          // Change it first: the call goes on beside the form on the web, so
          // the voice says nothing; the card is only taken off the ledger.
          sayBeforeLeaving: (toolCallId) => {
            if (era.current === mine) socket.current?.cardResult({ toolCallId, state: 'edit_first', receipt: null });
            return Promise.resolve();
          },
        });
      };

      /** One socket for the call: the first, or a try at coming back after a drop. */
      const connect = (resume: { readonly callId: string; readonly token: string } | null): void => {
        // The duplex this browser earned, from what its track read back (G5).
        const echoCancel = made.echoCancel();
        const context = liveStartContextOf(base, {
          device,
          resume,
          takeover: options?.takeover === true,
          duplex: webDuplexOf({ device, echoCancel }),
          echoCancel,
          sensitivity: useLiveSensitivityStore.getState().level,
        });
        const self: { socket: WebAssistantLiveSocket | null } = { socket: null };
        const opened = connectAssistantLive(tenantId, context, {
          onAudio: (pcm) => {
            if (era.current !== mine || socket.current !== self.socket || cutting.current) return;
            if (firstPlaybackAt.current === null) firstPlaybackAt.current = Date.now();
            made.play(pcm);
            speakLevel(pcm);
          },
          onMessage: (message) => {
            if (era.current === mine && socket.current === self.socket) receive(message, opened);
          },
          onClose: (code, reason) => {
            if (era.current !== mine || socket.current !== self.socket) return;
            dropped(code, reason);
          },
          onError: () => {
            /* A close always follows, and that is what ends the call or brings it back. */
          },
        }, seat);
        self.socket = opened;
        socket.current = opened;
      };

      /** The socket closed with no `closed` frame first: a version-2 call comes back inside its grace; anything else ends. */
      const dropped = (code: number, reason: string): void => {
        const resume = resumable.current;
        // A try at coming back that the server refuses ('nothing to resume') is the connection lost.
        if (reconnecting.current !== null && !isDrop(code)) {
          finish('lost');
          return;
        }
        if (wireNow.current !== 'v2' || resume === null || !isDrop(code)) {
          finish(reason === '' ? `socket ${code}` : reason);
          return;
        }
        let back = reconnecting.current;
        if (back === null) {
          back = {
            droppedAt: Date.now(),
            attempt: 0,
            timer: null,
            deadline: setTimeout(() => finish('lost'), LIVE_RECONNECT_GRACE_MS),
          };
          reconnecting.current = back;
          // OS's voice stops with the connection; what was on its way is dropped.
          made.flush();
          hush();
          setConnection('reconnecting');
        }
        const wait = reconnectDelayOf(back.attempt, Date.now() - back.droppedAt);
        if (wait === null) {
          finish('lost');
          return;
        }
        const held = back;
        held.timer = setTimeout(() => {
          held.timer = null;
          if (era.current !== mine || reconnecting.current !== held) return;
          held.attempt += 1;
          connect(resume);
        }, wait);
      };

      void onMicQueue(async () => {
        if (era.current !== mine) return;
        /** Joined into the wire's 100 ms frames: sent, or kept in the ring while the call comes back. */
        const goUp = (frames: readonly Uint8Array[]): void => {
          for (const frame of frames) {
            for (const wire of joiner.current.push(frame)) {
              if (reconnecting.current !== null) ring.current.push(wire as Uint8Array<ArrayBuffer>);
              else socket.current?.sendAudio(wire as Uint8Array<ArrayBuffer>);
            }
          }
        };
        endForNoMic.current = () => {
          called.current.onError('NO_MIC', micSentence('no_mic'));
          // The divider names an interruption; the thread's note says the microphone went (NO_MIC).
          finish('interrupted');
        };
        await made.start(
          (pcm) => {
            if (era.current !== mine) return;
            const now = Date.now();
            preRoll.current = preRollPush(preRoll.current, pcm, WEB_FRAME_MS, WEB_PRE_ROLL_MS);
            if (wireNow.current === 'v2') {
              const playingNow = made.isSpeaking();
              // A full-duplex call's gate is Mute alone; half duplex (or a call its echo dropped) waits out OS's voice and its grace.
              const fullNow = duplexNow.current === 'full' && !echoNow.current.echo;
              const held = muted.current || webAudio.current.interrupted;
              const blocks: EarBlock[] = held ? [] : blockLevels(pcm, LIVE_INPUT_RATE).map((block) => ({ db: block.db, floorDb: room.current.feed(block.db), ms: block.ms }));
              const open = !held && (fullNow || halfDuplexHeard({ muted: false, playing: playingNow, now, quietUntil: quietUntil.current }));
              const heard = liveEarOf(earModel.current, {
                blocks,
                verdict: open ? (reconnecting.current !== null ? 'buffer' : 'send') : 'drop',
                muted: held,
                playing: playingNow,
                full: fullNow,
                now,
                settling: duckSettlingOf({ firstPlaybackAt: firstPlaybackAt.current, now }),
                offline: reconnecting.current !== null,
                // The browser's mic runs automatic gain (microphone.ts): its level says nothing of how near a person is (7 Oct, item 1).
                nearKnown: false,
              });
              earModel.current = heard.model;
              stepTyping(now);
              if (heard.vad !== null) socket.current?.frame({ type: 'vad', ...heard.vad });
              // The duck: the voice's own gain, within 150 ms; the look says so.
              if (heard.duck) made.duck();
              else made.unduck();
              setDucked(heard.duck);
              setLoud(heard.model.ear.loud);
              if (held) {
                joiner.current.drop();
                return;
              }
              // The dot grid hears every unmuted frame, side talk included.
              const mic = called.current.micLevel;
              if (mic !== undefined) writeLevelSteps(mic, blocks.map((block) => ({ value: levelOf(block.db, block.floorDb), ms: block.ms })));
              if (!open) {
                joiner.current.drop();
                return;
              }
              // The gate opened on speech already begun: its start goes up first.
              goUp(heard.preRollMs > 0 ? preRollTake(preRoll.current, heard.preRollMs + WEB_FRAME_MS) : [pcm]);
              return;
            }
            if (!legacyMicHeard({ muted: muted.current, hold: hold.current, playing: made.isSpeaking(), now, quietUntil: quietUntil.current })) {
              joiner.current.drop();
              return;
            }
            const steps = micSteps(pcm);
            writeLevelSteps(called.current.level, steps);
            const mic = called.current.micLevel;
            if (mic !== undefined) writeLevelSteps(mic, steps);
            goUp([pcm]);
          },
          {
            // 50 ms frames, so the duck lands within 150 ms (Phase 0, 0.5 (d)); the wire keeps 100 ms.
            frameSamples: (LIVE_INPUT_RATE * WEB_FRAME_MS) / 1000,
            onTrack: (what) => {
              if (era.current === mine) audioEvent({ type: 'track', what });
            },
            onContextState: (state) => {
              if (era.current === mine) audioEvent({ type: 'context', state });
            },
          },
        );
        made.onStateChange((state) => {
          if (era.current === mine) audioEvent({ type: 'context', state });
        });
        if (era.current !== mine) {
          await made.stop();
          return;
        }
        connect(null);
        // The cards on screen reach this call while its socket is open: a card
        // changed by hand moves the call's ledger, so the voice never offers
        // what the person already answered.
        bridge();
      }).catch((caught: unknown) => {
        if (era.current !== mine) return;
        era.current = mine + 1;
        running.current = false;
        letWireGo();
        wakeLock.stop();
        letCardsGo();
        hush();
        setConnection('closed');
        setState('closed');
        const failure = micFailureOf(caught);
        called.current.onError(failure === 'mic_off' ? 'MIC_OFF' : failure === 'no_mic' ? 'NO_MIC' : 'FAILED', micSentence(failure));
        void onMicQueue(() => made.stop()).catch(() => undefined);
      });
    },
    [hush, letCardsGo, letWireGo, owe, settle, stepTyping, waiting, wakeLock],
  );

  /**
   * The call ends here. `keep`: the socket stays open for what the server
   * still owes, and only its turns are read. Otherwise every socket closes.
   */
  const end = useCallback(
    (keep: boolean, quiet = false) => {
      const wasUp = running.current;
      era.current += 1;
      running.current = false;
      working.current = false;
      askingNow.current = null;
      hold.current = null;
      cutting.current = false;
      const wasReconnecting = reconnecting.current !== null;
      letWireGo();
      wakeLock.stop();
      letCardsGo();
      const held = socket.current;
      socket.current = null;
      if (held !== null && keep && !wasReconnecting) {
        owe(held, true);
      } else if (held !== null) {
        held.stop();
        held.close();
      }
      if (!keep) {
        for (const one of owing.current) one.close();
        owing.current.clear();
        setOwed(false);
      }
      const made = audio.current;
      if (made !== null && wasUp && openedAt.current !== null && !quiet) made.earcon(LIVE_END_SOUND);
      if (made !== null) void onMicQueue(() => made.stop()).catch(() => undefined);
      hush();
      heardTrack.current = NEW_HEARD;
      saidParts.current = '';
      saidFresh.current = true;
      setHearing(null);
      setWork(NO_CALL_WORK);
      setPlaying(false);
      setConnection('closed');
      setState('idle');
    },
    [hush, letCardsGo, letWireGo, owe, wakeLock],
  );
  const stop = useCallback((options?: AssistantLiveStop) => end(false, options?.quiet === true), [end]);
  const stopLive = useCallback((): number => {
    // Stop live is the other way the "Tap to mute" tip is learnt.
    markSeen('muteTipSeen');
    setMuteTip(false);
    const ran = openedAt.current === null ? 0 : Date.now() - openedAt.current;
    end(true);
    return ran;
  }, [end]);

  const toggleMute = useCallback(() => {
    markSeen('muteTipSeen');
    setMuteTip(false);
    const next = !muted.current;
    muted.current = next;
    setMutedShown(next);
    socket.current?.mute(next);
    if (next) hush();
    if (wireNow.current !== 'v2') settle(waiting());
  }, [hush, settle, waiting]);

  /** Cut OS's speech here: what is scheduled stops now, and what is still on its way is not played. */
  const cutVoice = useCallback((): void => {
    const made = audio.current;
    if (made === null || !made.isSpeaking()) return;
    cutting.current = true;
    made.flush();
    hush();
    saidFresh.current = true;
  }, [hush]);

  /** Legacy: the live button's click, in every state. */
  const legacyTalk = useCallback(() => {
    const held = socket.current;
    if (held === null) return;
    if (muted.current) {
      muted.current = false;
      setMutedShown(false);
      held.mute(false);
    }
    cutVoice();
    // The click is the person's turn: no echo to wait out, and the mic is
    // taken as open now; the server's `mic` follows and confirms it.
    quietUntil.current = 0;
    hold.current = 'open';
    held.interrupt();
    settle(waiting(false));
  }, [cutVoice, settle, waiting]);

  const interrupt = useCallback(() => {
    if (wireNow.current !== 'v2') {
      legacyTalk();
      return;
    }
    // How much was heard goes first, so the cut line ends where the person stopped hearing it.
    const made = audio.current;
    if (made !== null && made.isSpeaking()) socket.current?.frame({ type: 'playback', playedMs: made.playedMs(), state: 'idle' });
    cutVoice();
    // The person wants to talk: no echo to wait out. Mute stays as they left it.
    quietUntil.current = 0;
    socket.current?.interrupt();
  }, [cutVoice, legacyTalk]);

  /** One of the person's named moves; a legacy call has none, and a reconnecting one drops it. */
  const command = useCallback((name: LiveCommand): void => {
    if (wireNow.current !== 'v2') return;
    socket.current?.frame(LIVE_COMMAND_FRAMES[name]);
  }, []);
  const attend = useCallback(() => command('attend'), [command]);
  /** Resume: the audio after an interruption (this click is the gesture a browser wants), else the parked request. */
  const resumeWork = useCallback(() => {
    if (!webAudio.current.interrupted) {
      command('resumeWork');
      return;
    }
    const made = audio.current;
    if (made === null) return;
    const mine = era.current;
    void made
      .resume()
      .then(() => {
        if (era.current === mine) audioEvent({ type: 'resumed' });
      })
      .catch(() => undefined);
  }, [command]);
  const exitQuiet = useCallback(() => command('exitQuiet'), [command]);
  const imHere = useCallback(() => command('imHere'), [command]);
  /** "Was that for me?" clicked: the utterance it named is claimed, and the chip goes. */
  const claim = useCallback(() => {
    if (claimable !== null && wireNow.current === 'v2') socket.current?.frame({ type: 'claim', utteranceId: claimable });
    if (claimTimer.current !== null) clearTimeout(claimTimer.current);
    claimTimer.current = null;
    setClaimable(null);
  }, [claimable]);
  const carryOn = useCallback(() => command('carryOn'), [command]);
  const continueHere = useCallback(() => {
    if (running.current) end(false);
    start({ takeover: true });
  }, [end, start]);

  const talk = useCallback(() => {
    if (wireNow.current !== 'v2') {
      legacyTalk();
      return;
    }
    if (audio.current?.isSpeaking() === true) interrupt();
    else attend();
  }, [attend, interrupt, legacyTalk]);

  const cancel = useCallback((messageId?: string) => {
    const held = socket.current;
    if (held !== null) {
      held.cancel(messageId);
      return;
    }
    // Stop reaches a call that ended for the conversation and still drains.
    for (const one of owing.current) one.cancel(messageId);
  }, []);

  const isTalking = useCallback(() => audio.current?.isSpeaking() ?? false, []);
  const say = useCallback((text: string, unsent?: () => void) => {
    if (wireNow.current === 'v2' && reconnecting.current !== null) {
      // Taken: it goes once the call is back. A call that ends first gives it
      // back through `unsent`, for the caller to send the typed way.
      void sayLater.current.hold(text).then((sent) => {
        if (!sent) unsent?.();
      });
      return true;
    }
    return socket.current?.say(text) ?? false;
  }, []);
  const files = useCallback((fileIds: readonly string[]) => socket.current?.files(fileIds), []);
  const place = useCallback((next: Pick<AssistantLiveContext, 'screen' | 'recordId' | 'cards' | 'view'>) => socket.current?.place(next), []);
  const appNote = useCallback((kind: LiveAppNote, text: string): boolean => {
    if (wireNow.current !== 'v2') return false;
    return socket.current?.frame({ type: 'app_note', kind, text }) ?? false;
  }, []);
  const scope = useCallback((keys: readonly string[]): boolean => {
    if (wireNow.current !== 'v2') return false;
    return socket.current?.frame({ type: 'setup_scope', keys }) ?? false;
  }, []);

  useEffect(() => () => stop(), [stop]);
  // The sensitivity reaches a call that is up at once (option B, step 6); the next call sends it in its start.
  useEffect(
    () =>
      useLiveSensitivityStore.subscribe((now, before) => {
        if (now.level !== before.level && wireNow.current === 'v2') socket.current?.frame({ type: 'sensitivity', level: now.level });
      }),
    [],
  );
  useEffect(
    () => () => {
      if (nameHintTimer.current !== null) clearTimeout(nameHintTimer.current);
      if (claimTimer.current !== null) clearTimeout(claimTimer.current);
    },
    [],
  );

  const up = state !== 'idle' && state !== 'closed';
  const v2 = wire === 'v2';
  // A version-2 call's look is the server's state; before `open`, and on a
  // legacy call, the same model reads the call's own turn, so the pill draws
  // every call alike (a legacy call has no ask chip: a click on the orb talks).
  const look = up
    ? webLookOf({
        connection: v2 ? connection : state === 'connecting' ? 'connecting' : 'open',
        v2,
        legacyTurn: state === 'working' ? 'working' : state === 'speaking' ? 'speaking' : 'listening',
        server,
        duplex,
        muted: mutedShown,
        playing,
        micDenied: false,
        muteTip,
        echo: echoShown.echo,
        earphonesNote: echoShown.earphonesNote,
        ducked,
        loud,
        interrupted,
        // Needs a click names the save words in the language the person speaks to OS (7 Oct, item 4).
        saveLanguage: spokenLanguageOf(
          transcript.rows.flatMap((row) => (row.role === 'you' ? [{ text: row.text, ...(row.spoken === undefined ? {} : { spoken: row.spoken }) }] : [])),
          callLanguage,
        ),
        nameHint,
        windowOpen,
        claim: claimable !== null,
        ...(endsInMs === undefined ? {} : { endsInMs }),
      })
    : null;

  // `devicechange` while a call runs: the microphone gone ends it (no_mic); earphones in bring a call its echo
  // dropped back to full (steps 4.3 and 4.4).
  const v2Call = up && v2;
  useEffect(() => {
    const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (!v2Call || devices === undefined || typeof devices.enumerateDevices !== 'function') return undefined;
    const changed = (): void => {
      void devices
        .enumerateDevices()
        .then((list) => audioEvent({ type: 'devices', mic: list.some((one) => one.kind === 'audioinput'), earphones: webEarphonesOf(list) }))
        .catch(() => undefined);
    };
    devices.addEventListener('devicechange', changed);
    return () => devices.removeEventListener('devicechange', changed);
  }, [v2Call, audioEvent]);

  // Each state a look enters plays its earcon once, beside the voice (the rising chime only as the call first listens).
  const lookState = look?.state ?? null;
  // The typing dots read the state and the person's newest line as they change, not only on the next mic frame.
  const newestLine = lastPersonLineOf(transcript.rows);
  const lineWriting = personLineWritingOf(transcript.rows);
  useEffect(() => {
    typingState.current = lookState;
    typingLine.current = newestLine;
    typingWriting.current = lineWriting;
    stepTyping(Date.now());
  }, [lookState, newestLine, lineWriting, stepTyping]);
  const cued = useRef<{ was: LiveStateName | null; risen: boolean }>({ was: null, risen: false });
  useEffect(() => {
    if (look === null) {
      cued.current = { was: null, risen: false };
      return;
    }
    const step = earconStep({ was: cued.current.was, look, risen: cued.current.risen, carried: carried.current });
    cued.current = { was: look.state, risen: step.risen };
    if (step.sound !== null) audio.current?.earcon(step.sound);
    // Only a new state plays; the look's other fields change nothing here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookState]);

  useLiveAttention({
    active: v2 && up,
    minimised: input.minimised === true,
    stateWord: look === null || !v2 ? null : tOr(look.word, ''),
    inView: cardsInView,
    onCardsChange: onViewChanged,
    onAttention: (frame) => {
      attentionNow.current = frame;
      if (reconnecting.current === null) socket.current?.frame(frame);
    },
  });

  return {
    state: v2 && look !== null ? turnOfLook(look) : state,
    wire,
    look,
    transcript,
    elsewhere,
    since,
    callId,
    hearing: wire === 'v2' ? liveHearingOf(transcript) : hearing,
    heard,
    caption,
    active: up,
    owed,
    start,
    stop,
    stopLive,
    toggleMute,
    interrupt,
    attend,
    resumeWork,
    exitQuiet,
    imHere,
    claimable,
    typing: v2 && typing,
    claim,
    carryOn,
    continueHere,
    cancel,
    talk,
    isTalking,
    work,
    say,
    files,
    place,
    appNote,
    scope,
  };
};
